import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { Search, Building, X, Check, ChevronLeft, ChevronRight, Plus, Loader2, Trash2, Trophy } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface School {
    id: number;
    school_id: number;
    name: string;
    slug: string;
    city: string;
    area: string;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const RelatedSchools: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    // Available schools state
    const [availableSchools, setAvailableSchools] = useState<School[]>([]);
    const [isLoadingSchools, setIsLoadingSchools] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50,
    });

    // Selected schools state
    const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
    const [relatedSchools, setRelatedSchools] = useState<School[]>([]);
    const [_isLoadingMappings, setIsLoadingMappings] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Competitive schools state
    const [competitiveSchools, setCompetitiveSchools] = useState<{ id: number; name: string }[]>([]);
    const [competitiveInput, setCompetitiveInput] = useState('');
    const [addingCompetitive, setAddingCompetitive] = useState(false);
    const [deletingCompetitiveId, setDeletingCompetitiveId] = useState<number | null>(null);

    // Fetch available schools
    const fetchAvailableSchools = async (page: number = 1) => {
        try {
            setIsLoadingSchools(true);
            const response = await apiRequest(`/related-schools/available?page=${page}&limit=50&search=${encodeURIComponent(searchTerm)}&exclude_id=${id}`);

            if (response.success) {
                setAvailableSchools(response.data.schools || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch schools', 'error');
            }
        } catch (error) {
            console.error('Error fetching schools:', error);
            showToast('Failed to fetch schools', 'error');
        } finally {
            setIsLoadingSchools(false);
        }
    };

    // Fetch related schools
    const fetchRelatedSchools = async () => {
        try {
            setIsLoadingMappings(true);
            const response = await apiRequest(`/related-schools/${id}`);

            if (response.success) {
                const related = response.data.relatedSchools || [];
                setRelatedSchools(related);
                setSelectedSchoolIds(related.map((s: School) => s.id));
            } else {
                showToast('Failed to fetch related schools', 'error');
            }
        } catch (error) {
            console.error('Error fetching related schools:', error);
            showToast('Failed to fetch related schools', 'error');
        } finally {
            setIsLoadingMappings(false);
        }
    };

    const fetchCompetitiveSchools = async () => {
        try {
            const response = await apiRequest(`/related-schools/${id}/competitive`);
            if (response.success) setCompetitiveSchools(response.data.competitiveSchools || []);
        } catch (err) {
            console.error('Error fetching competitive schools:', err);
        }
    };

    const handleAddCompetitive = async () => {
        if (!competitiveInput.trim()) {
            showToast('Please enter a school name', 'error');
            return;
        }
        try {
            setAddingCompetitive(true);
            const response = await apiRequest(`/related-schools/${id}/competitive`, {
                method: 'POST',
                body: JSON.stringify({ name: competitiveInput.trim() }),
            });
            if (response.success) {
                showToast('Competitive school added!', 'success');
                setCompetitiveInput('');
                fetchCompetitiveSchools();
            } else {
                showToast(response.message || 'Failed to add', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to add', 'error');
        } finally {
            setAddingCompetitive(false);
        }
    };

    const handleDeleteCompetitive = async (entryId: number) => {
        try {
            setDeletingCompetitiveId(entryId);
            const response = await apiRequest(`/related-schools/${id}/competitive/${entryId}`, { method: 'DELETE' });
            if (response.success) {
                showToast('Removed!', 'success');
                setCompetitiveSchools(prev => prev.filter(s => s.id !== entryId));
            } else {
                showToast(response.message || 'Failed to remove', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to remove', 'error');
        } finally {
            setDeletingCompetitiveId(null);
        }
    };

    // Initial load
    useEffect(() => {
        fetchAvailableSchools(1);
        fetchRelatedSchools();
        fetchCompetitiveSchools();
    }, [id]);

    // Search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAvailableSchools(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const MAX_RELATED_SCHOOLS = 10;

    // Handle school selection
    const handleSchoolToggle = (schoolId: number) => {
        setSelectedSchoolIds(prev => {
            if (prev.includes(schoolId)) {
                return prev.filter(id => id !== schoolId);
            } else {
                if (prev.length >= MAX_RELATED_SCHOOLS) {
                    showToast(`You can only select up to ${MAX_RELATED_SCHOOLS} related schools`, 'error');
                    return prev;
                }
                return [...prev, schoolId];
            }
        });
    };

    // Handle remove from selected
    const handleRemoveSelected = (schoolId: number) => {
        setSelectedSchoolIds(prev => prev.filter(id => id !== schoolId));
    };

    // Handle save
    const handleSave = async () => {
        try {
            setIsSaving(true);
            const response = await apiRequest(`/related-schools/${id}`, {
                method: 'POST',
                body: JSON.stringify({
                    school_ids: selectedSchoolIds,
                }),
            });

            if (response.success) {
                showToast('Related schools saved successfully', 'success');
                fetchRelatedSchools();
            } else {
                showToast('Failed to save related schools', 'error');
            }
        } catch (error) {
            console.error('Error saving related schools:', error);
            showToast('Failed to save related schools', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Get selected school details
    const getSelectedSchoolDetails = () => {
        const allSchools = [...relatedSchools, ...availableSchools];
        return selectedSchoolIds.map(schoolId => {
            const school = allSchools.find(s => s.id === schoolId);
            return school || { id: schoolId, school_id: schoolId, name: `School #${schoolId}`, slug: '', city: '', area: '' };
        });
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Building className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Related Schools</h1>
                            <p className="text-xs text-gray-500">Attach related schools to this school</p>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Available Schools + Competitive Schools */}
                    <div className="flex flex-col gap-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4">Available Schools</h2>

                        {/* Search */}
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search schools..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Schools List */}
                        <div className="space-y-2 max-h-[320px] overflow-y-auto">
                            {isLoadingSchools ? (
                                <p className="text-sm text-gray-500 text-center py-4">Loading schools...</p>
                            ) : availableSchools.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No schools found</p>
                            ) : (
                                availableSchools.map((school) => {
                                    const isSelected = selectedSchoolIds.includes(school.id);
                                    return (
                                        <div
                                            key={school.id}
                                            onClick={() => handleSchoolToggle(school.id)}
                                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                                isSelected
                                                    ? 'bg-blue-50 border-blue-300'
                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                                                isSelected
                                                    ? 'bg-brandColor border-brandColor'
                                                    : 'border-gray-300'
                                            }`}>
                                                {isSelected && <Check size={12} className="text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{school.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    ID: {school.school_id || school.id}
                                                    {school.area && ` • ${school.area}`}
                                                    {school.city && school.city !== school.area && `, ${school.city}`}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <span className="text-xs text-gray-500">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchAvailableSchools(pagination.currentPage - 1)}
                                        disabled={pagination.currentPage === 1}
                                    >
                                        <ChevronLeft size={14} />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchAvailableSchools(pagination.currentPage + 1)}
                                        disabled={pagination.currentPage === pagination.totalPages}
                                    >
                                        <ChevronRight size={14} />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Competitive Schools - manual text below Available Schools */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Trophy size={16} className="text-yellow-500" />
                            Competitive Schools
                        </h2>

                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={competitiveInput}
                                onChange={(e) => setCompetitiveInput(e.target.value)}
                                onKeyPress={(e) => { if (e.key === 'Enter') handleAddCompetitive(); }}
                                placeholder="Type school name..."
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-brandColor focus:border-transparent"
                            />
                            <button
                                onClick={handleAddCompetitive}
                                disabled={addingCompetitive || !competitiveInput.trim()}
                                className="inline-flex items-center gap-1 px-3 py-2 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                {addingCompetitive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus size={14} />}
                                Add
                            </button>
                        </div>

                        <div className="space-y-2 max-h-[260px] overflow-y-auto">
                            {competitiveSchools.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No competitive schools added yet</p>
                            ) : (
                                competitiveSchools.map((school) => (
                                    <div key={school.id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <Trophy size={13} className="text-yellow-500 flex-shrink-0" />
                                            <span className="text-sm font-medium text-gray-900 truncate">{school.name}</span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteCompetitive(school.id)}
                                            disabled={deletingCompetitiveId === school.id}
                                            className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            {deletingCompetitiveId === school.id
                                                ? <Loader2 size={14} className="animate-spin" />
                                                : <Trash2 size={14} />}
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    </div>{/* end left column wrapper */}

                    {/* Right Column - Selected Schools */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            Selected Schools ({selectedSchoolIds.length}/{MAX_RELATED_SCHOOLS})
                        </h2>

                        {/* Selected Schools List */}
                        <div className="space-y-2 max-h-[320px] overflow-y-auto">
                            {selectedSchoolIds.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No schools selected</p>
                            ) : (
                                getSelectedSchoolDetails().map((school) => (
                                    <div
                                        key={school.id}
                                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{school.name}</p>
                                            <p className="text-xs text-gray-500">
                                                ID: {school.school_id || school.id}
                                                {school.area && ` • ${school.area}`}
                                                {school.city && school.city !== school.area && `, ${school.city}`}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSelected(school.id)}
                                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RelatedSchools;
